import React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import './Index.less';
import HwzComponent from '../../common/component/HwzComponent';
import HwzTitle from '../widget/HwzTitle';
import { Alert, Col, Row, Tabs } from 'antd';
import HwzContentCard from '../widget/HwzContentCard';
import RatePanel from './widget/RatePanel';
import * as echarts from 'echarts';
import theme from './theme.json';
import Pager from '../../common/model/base/Pager';
import Dashboard from '../../common/model/dashboard/Dashboard';
import DateUtil from '../../common/util/DateUtil';
import SortDirection from '../../common/model/base/SortDirection';
import FileUtil from '../../common/util/FileUtil';
import Matter from '../../common/model/matter/Matter';
import Lang from '../../common/model/global/Lang';
import MessageBoxUtil from '../../common/util/MessageBoxUtil';
import HaStatus from '../../common/model/ha/HaStatus';

const { TabPane } = Tabs;
echarts.registerTheme('Hwz_theme', theme);

interface IProps extends RouteComponentProps {}

interface IState {
  refreshKey: number;
  isMounted: boolean;
  invokeListOption: SimpleChartOption;
}

interface IpStruct {
  ip: string;
  times: number;
}

// 简化图表选项类型
interface SimpleChartOption {
  tooltip?: any;
  legend?: any;
  xAxis?: any;
  yAxis?: any;
  series?: any;
  [key: string]: any;
}

export default class Index extends HwzComponent<IProps, IState> {
  private echartsInstance: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private chartContainerRef = React.createRef<HTMLDivElement>();
  private unlisten: any = null;
  private haStatusTimer: any = null;

  pager: Pager<Dashboard> = new Pager<Dashboard>(this, Dashboard, 15);
  activeName: string = 'first';
  dashboard: Dashboard = new Dashboard(this);
  yesterdayDashboard: Dashboard = new Dashboard(this);
  haStatus: HaStatus = new HaStatus(this);
  matterPager: Pager<Matter> = new Pager<Matter>(this, Matter, 10);
  activeIpTop10: IpStruct[] = [];

  days: number = 15;
  dateStrings: string[] = [];

  standardWeekInvokeNum: number = 0;
  compareWeekInvokeNum: number = 0;
  standardDayInvokeNum: number = 0;
  compareDayInvokeNum: number = 0;
  standardWeekUv: number = 0;
  compareWeekUv: number = 0;
  standardDayUv: number = 0;
  compareDayUv: number = 0;
  standardWeekMatterNum: number = 0;
  compareWeekMatterNum: number = 0;
  standardDayMatterNum: number = 0;
  compareDayMatterNum: number = 0;
  standardWeekSize: number = 0;
  compareWeekSize: number = 0;
  standardDaySize: number = 0;
  compareDaySize: number = 0;

  constructor(props: IProps) {
    super(props);
    this.state = {
      refreshKey: 0,
      isMounted: false,
      invokeListOption: {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any) => {
            let result = params[0].name + '<br/>';
            params.forEach((item: any) => {
              result += item.marker + ' ' + item.seriesName + ': ' + item.value + '<br/>';
            });
            return result;
          }
        },
        legend: { data: ['PV', 'UV'] },
        xAxis: {
          name: Lang.t('dashboard.date'),
          data: Array.from({ length: 15 }, (_, i) => i + 1)
        },
        yAxis: { name: Lang.t('dashboard.num') },
        series: [
          {
            name: 'PV',
            type: 'bar',
            data: new Array(15).fill(0),
            itemStyle: { color: '#1890ff' },
            emphasis: {
              focus: 'series',
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          },
          {
            name: 'UV',
            type: 'line',
            data: new Array(15).fill(0),
            itemStyle: { color: '#52c41a' },
            lineStyle: { width: 3 },
            symbol: 'circle',
            symbolSize: 8,
            emphasis: {
              focus: 'series',
              itemStyle: {
                borderWidth: 2,
                borderColor: '#fff',
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
    };
  }

  componentDidMount() {
    this.setState({ isMounted: true }, () => {
      this.initChart();
      this.refresh();

      // 定时检测备机是否接管，保证告警能及时触发
      this.haStatusTimer = setInterval(() => {
        if (this.state.isMounted) {
          this.refreshHaStatus();
        }
      }, 30000);

      this.unlisten = this.props.history.listen((location) => {
        if (this.state.isMounted && location.pathname === this.props.location.pathname) {
          this.refresh();
        }
      });
    });
  }

  componentWillUnmount() {
    if (this.haStatusTimer) {
      clearInterval(this.haStatusTimer);
      this.haStatusTimer = null;
    }
    this.setState({ isMounted: false });
    this.cleanupChart();

    if (this.unlisten) {
      this.unlisten();
    }
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.state.isMounted && this.props.location.key !== prevProps.location.key) {
      this.refresh();
    }
  }

  private initChart() {
    if (!this.chartContainerRef.current || !this.state.isMounted) return;

    this.cleanupChart();

    try {
      this.echartsInstance = echarts.init(this.chartContainerRef.current, 'Hwz_theme');
      this.echartsInstance.setOption(this.state.invokeListOption);
      this.initResizeObserver();
    } catch (error) {
      console.error('ECharts初始化失败:', error);
    }
  }

  private initResizeObserver() {
    if (!this.chartContainerRef.current || !this.echartsInstance) return;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    try {
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          if (this.echartsInstance && this.state.isMounted) {
            try {
              this.echartsInstance.resize();
            } catch (error) {
              console.warn('图表重绘失败:', error);
            }
          }
        });
      });

      this.resizeObserver.observe(this.chartContainerRef.current);
    } catch (error) {
      console.error('ResizeObserver初始化失败:', error);
    }
  }

  private cleanupChart() {
    if (this.resizeObserver) {
      try {
        this.resizeObserver.disconnect();
      } catch (error) {
        console.warn('ResizeObserver清理失败:', error);
      }
      this.resizeObserver = null;
    }

    if (this.echartsInstance) {
      try {
        if (!this.echartsInstance.isDisposed) {
          this.echartsInstance.dispose();
        }
      } catch (error) {
        console.warn('ECharts实例清理失败:', error);
      }
      this.echartsInstance = null;
    }
  }

  refresh() {
    if (!this.state.isMounted) return;

    this.updateDateStrings();
    this.refreshDashboardPager();
    this.refreshMatterPager();
    this.refreshActiveIpTop10();
    this.refreshHaStatus();
    this.setState({ refreshKey: Date.now() });
  }

  updateDateStrings() {
    this.dateStrings = Array.from({ length: this.days }, (_, d) => {
      const thenDate = new Date(Date.now() - (this.days - 1 - d) * 24 * 60 * 60 * 1000);
      return DateUtil.simpleDate(thenDate);
    });
  }

  // 标准化日期函数，确保返回 YYYY-MM-DD 格式的日期字符串
  private normalizeDate(dtStr: string): string {
    const match = dtStr.match(/(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})/);
    if (match) {
      const [, y, m, d] = match;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return dtStr;
  }

  refreshDashboardPager() {
    let that = this;

    console.log('=== Debug Dashboard ===');
    console.log('Expected dateStrings:', that.dateStrings);

    this.pager.setFilterValue('orderDt', SortDirection.DESC);

    this.pager.httpList(function (response: any) {
      let list = that.pager.data;

      list.forEach((item, idx) => {
        console.log(`Item ${idx} dt: "${item.dt}" (type: ${typeof item.dt})`);
      });

      if (list.length > 0) that.dashboard.assign(list[0]);
      if (list.length > 1) that.yesterdayDashboard.assign(list[1]);

      let map: { [key: string]: Dashboard } = {};
      for (let i = 0; i < list.length; i++) {
        const dateKey = that.normalizeDate(list[i].dt);
        map[dateKey] = list[i];
      }

      let invokeNumData = Array(that.days).fill(0);
      let uvData = Array(that.days).fill(0);

      for (let i = 0; i < that.dateStrings.length; i++) {
        let item = map[that.dateStrings[i]];
        if (item) {
          invokeNumData[i] = item.invokeNum;
          uvData[i] = item.uv;
        }
      }

      // 更新状态中的图表配置
      that.setState(
        {
          invokeListOption: {
            ...that.state.invokeListOption,
            xAxis: {
              ...that.state.invokeListOption.xAxis,
              data: that.dateStrings.map(k => k.substr(5))
            },
            series: [
              { ...that.state.invokeListOption.series[0], data: invokeNumData },
              { ...that.state.invokeListOption.series[1], data: uvData }
            ]
          }
        },
        () => {
          // 状态更新后同步到 ECharts 实例
          if (that.echartsInstance) {
            that.echartsInstance.setOption(that.state.invokeListOption);
          }
        }
      );
    });
  }

  refreshMatterPager() {
    if (!this.state.isMounted) return;
    this.matterPager.setFilterValue('orderTimes', SortDirection.DESC);
    this.matterPager.httpList();
  }

  refreshActiveIpTop10() {
    if (!this.state.isMounted) return;

    this.dashboard.httpActiveIpTop10((data: any) => {
      if (!this.state.isMounted) return;
      if (data) this.activeIpTop10 = data;
      this.updateUI();
    });
  }

  refreshHaStatus() {
    if (!this.state.isMounted) return;

    this.haStatus.httpStatus((data: any) => {
      if (!this.state.isMounted) return;
      if (data) this.haStatus.assign(data);
      this.updateUI();
    });
  }

  getUsageColor(usage: number): string {
    if (usage >= 80) return '#ff4d4f';
    if (usage >= 60) return '#faad14';
    return '#52c41a';
  }

  getAvailableSpaceColor(availableSpace: number): string {
    if (!availableSpace) return '#666';
    const gb = availableSpace / (1024 * 1024 * 1024);
    if (gb < 10) return '#ff4d4f';
    if (gb < 50) return '#fa8c16';
    return '#52c41a';
  }

  reRun() {
    if (!this.state.isMounted) return;

    this.dashboard.httpEtl((data: any) => {
      if (!this.state.isMounted) return;
      MessageBoxUtil.success(Lang.t('operationSuccess'));
      this.refresh();
    });
  }

  render() {
    const { dashboard, yesterdayDashboard } = this;

    return (
      <div className="page-dashboard-index">
        {this.haStatus.takeover && (
          <Alert
            message={this.haStatus.message || Lang.t('haTakeover')}
            type="error"
            showIcon
            closable={false}
            style={{
              marginBottom: 16,
              fontWeight: 'bold'
            }}
          />
        )}
        <div
          className="dashboard-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16
          }}
        >
          <Alert
            message={
              <span>
                {Lang.t('dashboard.warnHint')}
                <span
                  className="link"
                  onClick={this.reRun.bind(this)}
                  style={{ marginLeft: 8 }}
                >
                  {Lang.t('dashboard.reRun')}
                </span>
              </span>
            }
            type="warning"
            showIcon
            style={{ flexShrink: 0, maxWidth: '60%' }}
          />
        </div>

        <HwzContentCard>
          <Tabs
            defaultActiveKey={this.activeName}
            onTabClick={(activeKey: string) => {
              this.activeName = activeKey;
              this.updateUI();
            }}
          >
            <TabPane tab={Lang.t('layout.dashboard')} key="dashboard">
              {/* 第一行：三个统计卡片 */}
              <Row gutter={18} style={{ marginBottom: 18 }}>
                {/* 第一个分组：调用统计 */}
                <Col xs={24} sm={24} md={12} lg={8}>
                  <div className="text-block">
                    <div className="upper">
                      <div
                        className="stats-row"
                        style={{ display: 'flex', justifyContent: 'space-between' }}
                      >
                        <div className="stat-item" style={{ flex: 1, textAlign: 'center' }}>
                          <div className="indicator">{Lang.t('dashboard.totalInvokeNum')}</div>
                          <div
                            className="amount"
                            style={{ fontSize: '24px', fontWeight: 'bold', margin: '8px 0' }}
                          >
                            {dashboard.totalInvokeNum}
                          </div>
                          <div
                            className="subtext"
                            style={{ fontSize: '12px', color: '#666' }}
                          >
                            {Lang.t('dashboard.yesterday')}: {yesterdayDashboard.invokeNum}
                          </div>
                        </div>
                        <div
                          className="stat-item bordered"
                          style={{
                            flex: 1,
                            textAlign: 'center',
                            borderLeft: '1px solid #f0f0f0',
                            borderRight: '1px solid #f0f0f0'
                          }}
                        >
                          <div className="indicator">{Lang.t('dashboard.totalUV')}</div>
                          <div
                            className="amount"
                            style={{ fontSize: '24px', fontWeight: 'bold', margin: '8px 0' }}
                          >
                            {dashboard.totalUv}
                          </div>
                          <div
                            className="subtext"
                            style={{ fontSize: '12px', color: '#666' }}
                          >
                            {Lang.t('dashboard.yesterday')}: {yesterdayDashboard.uv}
                          </div>
                        </div>
                        <div className="stat-item" style={{ flex: 1, textAlign: 'center' }}>
                          <div className="indicator">{Lang.t('dashboard.totalMatterNum')}</div>
                          <div
                            className="amount"
                            style={{ fontSize: '24px', fontWeight: 'bold', margin: '8px 0' }}
                          >
                            {dashboard.totalMatterNum}
                          </div>
                          <div
                            className="subtext"
                            style={{ fontSize: '12px', color: '#666' }}
                          >
                            {Lang.t('dashboard.yesterday')}: {yesterdayDashboard.matterNum}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="lower"
                      style={{
                        textAlign: 'center',
                        marginTop: '12px',
                        padding: '8px',
                        backgroundColor: '#fafafa',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#999'
                      }}
                    >
                      {Lang.t('dashboard.usageStats')}
                    </div>
                  </div>
                </Col>

                {/* 第二个分组：文件大小统计 */}
                <Col xs={24} sm={24} md={12} lg={8}>
                  <div className="text-block">
                    <div className="upper">
                      <div
                        className="size-stats"
                        style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}
                      >
                        <div className="size-item" style={{ flex: 1, textAlign: 'center' }}>
                          <div className="indicator">{Lang.t('dashboard.totalFileSize')}</div>
                          <div
                            className="amount"
                            style={{ fontSize: '18px', fontWeight: 'bold', margin: '8px 0' }}
                          >
                            {FileUtil.humanFileSize(dashboard.totalFileSize)}
                          </div>
                        </div>
                        <div
                          className="size-item bordered"
                          style={{
                            flex: 1,
                            textAlign: 'center',
                            borderLeft: '1px solid #f0f0f0'
                          }}
                        >
                          <div className="indicator">{Lang.t('dashboard.availableDiskSpace')}</div>
                          <div
                            className="amount"
                            style={{
                              fontSize: '18px',
                              fontWeight: 'bold',
                              margin: '8px 0',
                              color: this.getAvailableSpaceColor(dashboard.availableDiskSpaceSize)
                            }}
                          >
                            {FileUtil.humanFileSize(dashboard.availableDiskSpaceSize)}
                          </div>
                        </div>
                      </div>
                      <div className="rate-panels" style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <RatePanel
                            name={Lang.t('dashboard.weekRate')}
                            standardValue={this.standardWeekSize}
                            compareValue={this.compareWeekSize}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <RatePanel
                            name={Lang.t('dashboard.dayRate')}
                            standardValue={this.standardDaySize}
                            compareValue={this.compareDaySize}
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      className="lower"
                      style={{
                        textAlign: 'center',
                        marginTop: '12px',
                        padding: '8px',
                        backgroundColor: '#fafafa',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#999'
                      }}
                    >
                      {Lang.t('dashboard.yesterdayMatterSize')}: {FileUtil.humanFileSize(yesterdayDashboard.fileSize)}
                    </div>
                  </div>
                </Col>

                {/* 第三个分组：系统状态统计 */}
                <Col xs={24} sm={24} md={12} lg={8}>
                  <div className="text-block">
                    <div className="upper">
                      <div className="system-stats">
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div className="system-item" style={{ flex: 1, textAlign: 'center' }}>
                            <div className="indicator">{Lang.t('dashboard.cpuUsage')}</div>
                            <div
                              className="amount"
                              style={{
                                fontSize: '18px',
                                fontWeight: 'bold',
                                margin: '8px 0',
                                color: this.getUsageColor(dashboard.cpuUsage)
                              }}
                            >
                              {dashboard.cpuUsage?.toFixed(0)}%
                            </div>
                          </div>
                          <div
                            className="system-item bordered"
                            style={{
                              flex: 1,
                              textAlign: 'center',
                              borderLeft: '1px solid #f0f0f0',
                              borderRight: '1px solid #f0f0f0'
                            }}
                          >
                            <div className="indicator">{Lang.t('dashboard.memory')}</div>
                            <div
                              className="amount"
                              style={{
                                fontSize: '18px',
                                fontWeight: 'bold',
                                margin: '8px 0',
                                color: this.getUsageColor(dashboard.memoryUsage)
                              }}
                            >
                              {dashboard.memoryUsage?.toFixed(0)}%
                            </div>
                          </div>
                          <div
                            className="system-item bordered"
                            style={{
                              flex: 1,
                              textAlign: 'center',
                              borderRight: '1px solid #f0f0f0'
                            }}
                          >
                            <div className="indicator">{Lang.t('dashboard.storage')}</div>
                            <div
                              className="amount"
                              style={{
                                fontSize: '18px',
                                fontWeight: 'bold',
                                margin: '8px 0',
                                color: this.getUsageColor(dashboard.storageUsage)
                              }}
                            >
                              {dashboard.storageUsage?.toFixed(0)}%
                            </div>
                          </div>
                          <div className="system-item" style={{ flex: 1, textAlign: 'center' }}>
                            <div className="indicator">{Lang.t('dashboard.totalMemory')}</div>
                            <div
                              className="amount"
                              style={{
                                fontSize: '16px',
                                fontWeight: 'bold',
                                margin: '8px 0'
                              }}
                            >
                              {FileUtil.humanFileSize(dashboard.totalMemory)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="lower"
                      style={{
                        textAlign: 'center',
                        marginTop: '12px',
                        padding: '8px',
                        backgroundColor: '#fafafa',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#999'
                      }}
                    >
                      {Lang.t('dashboard.systemStatus')}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* 第二行：合并图表和排行榜为三列 */}
              <Row gutter={18}>
                {/* 第一列：图表 */}
                <Col xs={24} lg={14}>
                  <div className="figure-block">
                    <div
                      className="title"
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                    >
                      {Lang.t('dashboard.recentDayInvokeUV', 15)}
                    </div>
                    <div ref={this.chartContainerRef} style={{ height: '400px', width: '100%' }} />
                  </div>
                </Col>

                {/* 第二列：下载文件排行榜 */}
                <Col xs={24} lg={5}>
                  <div className="figure-block">
                    <div
                      className="title"
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                    >
                      {Lang.t('dashboard.downloadMatterTop10')}
                    </div>
                    <div className="list-rank">
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {this.matterPager.data.map((matter, index) => (
                          <li
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 0',
                              borderBottom: '1px solid #f5f5f5'
                            }}
                          >
                            <span
                              className={`rank ${index < 3 ? 'top3' : ''}`}
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: index < 3 ? '#1890ff' : '#f0f0f0',
                                color: index < 3 ? '#fff' : '#666',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                marginRight: '12px'
                              }}
                            >
                              {index + 1}
                            </span>
                            <Link
                              className="name"
                              to={`/matter/detail/${matter.uuid}`}
                              style={{
                                flex: 1,
                                color: '#1890ff',
                                textDecoration: 'none',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {matter.name}
                            </Link>
                            <span
                              className="info"
                              style={{
                                color: '#666',
                                fontSize: '12px',
                                marginLeft: '8px'
                              }}
                            >
                              {matter.times}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Col>

                {/* 第三列：活跃IP排行榜 */}
                <Col xs={24} lg={5}>
                  <div className="figure-block">
                    <div
                      className="title"
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                    >
                      {Lang.t('dashboard.activeIpTop10')}
                    </div>
                    <div className="list-rank">
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {this.activeIpTop10.map((item, index) => (
                          <li
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 0',
                              borderBottom: '1px solid #f5f5f5'
                            }}
                          >
                            <span
                              className={`rank ${index < 3 ? 'top3' : ''}`}
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: index < 3 ? '#1890ff' : '#f0f0f0',
                                color: index < 3 ? '#fff' : '#666',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                marginRight: '12px'
                              }}
                            >
                              {index + 1}
                            </span>
                            <span
                              className="name"
                              style={{
                                flex: 1,
                                color: '#333',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {item.ip}
                            </span>
                            <span
                              className="info"
                              style={{
                                color: '#666',
                                fontSize: '12px',
                                marginLeft: '8px'
                              }}
                            >
                              {item.times}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Col>
              </Row>
            </TabPane>
          </Tabs>
        </HwzContentCard>
      </div>
    );
  }
}